import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuSeparator, 
  DropdownMenuTrigger 
} from '@/components/ui/dropdown-menu';
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogFooter, 
  DialogHeader, 
  DialogTitle 
} from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { 
  MoreHorizontal, 
  RotateCcw, 
  Square, 
  FileText, 
  TrendingUp, 
  AlertCircle,
  Activity 
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useUserRole } from '@/hooks/useUserRole';
import { hasPermission, Permission } from '@/lib/rolePermissions';
import { useTranslation } from 'react-i18next';

interface Job {
  id: string;
  job_type: string;
  status: string;
  priority: number;
  error_message?: string;
}

interface JobActionsMenuProps {
  job: Job;
  onRetry: (jobId: string) => void;
  onCancel: (jobId: string) => void;
  onChangePriority: (jobId: string, priority: number) => void;
}

export function JobActionsMenu({ job, onRetry, onCancel, onChangePriority }: JobActionsMenuProps) {
  const [priorityDialogOpen, setPriorityDialogOpen] = useState(false);
  const [newPriority, setNewPriority] = useState(job.priority.toString());
  const { role } = useUserRole();
  const { toast } = useToast();
  const { t } = useTranslation('common');

  const canModifyJobs = hasPermission(role, Permission.CHANGE_FINDING_STATUS);
  const canViewLogs = hasPermission(role, Permission.UPLOAD_DOCUMENTS); // Basic permission

  const handlePriorityChange = () => {
    onChangePriority(job.id, parseInt(newPriority));
    setPriorityDialogOpen(false);
    toast({
      title: t('jobs.priority.changed'),
      description: `${t('jobs.priority.changed.description')} ${newPriority}`
    });
  };

  const openLogViewer = () => {
    // TODO: Implement log viewer
    toast({
      title: t('jobs.logViewer.title'),
      description: t('jobs.logViewer.description') + " " + job.id.slice(0, 8) + "..."
    });
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          {/* View Logs */}
          {canViewLogs && (
            <DropdownMenuItem onClick={openLogViewer}>
              <Activity className="h-4 w-4 mr-2" />
              {t('jobs.showLog')}
            </DropdownMenuItem>
          )}
          
          {/* Error Report - only for failed jobs */}
          {job.status === 'failed' && job.error_message && (
            <DropdownMenuItem>
              <AlertCircle className="h-4 w-4 mr-2" />
              {t('jobs.showError')}
            </DropdownMenuItem>
          )}

          <DropdownMenuSeparator />

          {/* Retry - only for failed jobs */}
          {job.status === 'failed' && canModifyJobs && (
            <DropdownMenuItem onClick={() => onRetry(job.id)}>
              <RotateCcw className="h-4 w-4 mr-2" />
              {t('jobs.retry')}
            </DropdownMenuItem>
          )}

          {/* Cancel - only for queued/running jobs */}
          {(job.status === 'queued' || job.status === 'running') && canModifyJobs && (
            <DropdownMenuItem onClick={() => onCancel(job.id)}>
              <Square className="h-4 w-4 mr-2" />
              {t('jobs.cancel')}
            </DropdownMenuItem>
          )}

          {/* Change Priority */}
          {(job.status === 'queued' || job.status === 'running') && canModifyJobs && (
            <DropdownMenuItem onClick={() => setPriorityDialogOpen(true)}>
              <TrendingUp className="h-4 w-4 mr-2" />
              {t('jobs.changePriority')}
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Priority Dialog */}
      <Dialog open={priorityDialogOpen} onOpenChange={setPriorityDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('jobs.changePriority')}</DialogTitle>
            <DialogDescription>
              {t('jobs.changePriority.description')}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">{t('jobs.currentPriority')}</label>
              <div className="mt-1">
                <Badge variant="outline">P{job.priority}</Badge>
              </div>
            </div>
            
            <div>
              <label className="text-sm font-medium">{t('jobs.newPriority')}</label>
              <Select value={newPriority} onValueChange={setNewPriority}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">{t('jobs.priority.low')}</SelectItem>
                  <SelectItem value="2">{t('jobs.priority.normal')}</SelectItem>
                  <SelectItem value="3">{t('jobs.priority.high')}</SelectItem>
                  <SelectItem value="4">{t('jobs.priority.critical')}</SelectItem>
                  <SelectItem value="5">{t('jobs.priority.urgent')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setPriorityDialogOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handlePriorityChange}>
              {t('common.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}